import React, { memo } from 'react';

import { useSelector } from 'react-redux';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { StyledBadge } from '@/components/atoms/StyledBadge/StyledBadge';
import {
  Avatar as ShadcnAvatar,
  AvatarFallback,
  AvatarImage,
  type AvatarSize,
} from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import useMembers from '@/hooks/useMembers';
import { cn } from '@/lib/utils';
import { selectAgentById } from '@/redux/presence/selectors';
import { RootState } from '@/shared/types/state';
import { getInitials } from '@/util/memberUtils';

import { UserInfoTooltipContent } from '../UserInfoTooltipContent/UserInfoTooltipContent';

type Props = {
  size?: 'small' | 'medium' | 'large';
  loading?: boolean;
  active?: boolean;
  children?: React.ReactNode;
  userId?: string;
  altText?: string;
  src?: string;
  hideStatus?: boolean;
  status?: 'online' | 'offline' | 'away';
  hideTooltip?: boolean;
};

const sizeMap: Record<NonNullable<Props['size']>, AvatarSize> = {
  small: 'xxs',
  medium: 'xs',
  large: 'sm',
};

const skeletonSizeClassMap = { small: 'size-4', medium: 'size-6', large: 'size-8' } as const;

const AvatarComponent = ({
  size = 'medium',
  loading = false,
  active = false,
  children,
  userId,
  altText,
  src,
  hideStatus = false,
  status,
  hideTooltip = false,
  ref,
}: Props & { ref?: React.Ref<HTMLSpanElement> }) => {
  const { getMember, listStatus: membersStatus } = useMembers();
  const user = getMember(userId);
  const agent = useSelector((state: RootState) => selectAgentById(state, user?.user_id));
  const statusState = status || agent?.status;

  if (loading) {
    return <Skeleton className={cn('rounded-full', skeletonSizeClassMap[size])} />;
  }

  const imageSrc = src ?? user?.avatar;
  const alt = altText ?? user?.name ?? '';
  const initials = getInitials(altText ?? user?.name);
  const shadcnSize = sizeMap[size];
  const needsBadge = !children && !src && statusState && !hideStatus;
  const activeClasses = active && 'bg-informational text-primary-foreground';

  const avatarInner = (
    <ShadcnAvatar
      size={shadcnSize}
      ref={needsBadge ? undefined : ref}
      className={cn(activeClasses)}
    >
      {children ?? (
        <>
          {imageSrc && <AvatarImage src={imageSrc} alt={alt} loading="lazy" />}
          <AvatarFallback size={shadcnSize} className={cn(activeClasses)}>
            {initials}
          </AvatarFallback>
        </>
      )}
    </ShadcnAvatar>
  );

  const avatarVisual = needsBadge ? (
    <StyledBadge
      ref={ref}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      status={statusState}
    >
      {avatarInner}
    </StyledBadge>
  ) : (
    avatarInner
  );

  const canShowTooltip =
    !!userId && !children && !src && !hideTooltip && (membersStatus === 'pending' || !!user);

  if (!canShowTooltip) {
    return avatarVisual;
  }

  return (
    <InfoTooltip
      variant="rich"
      size="auto"
      side="right"
      delayDuration={100}
      showArrow
      content={<UserInfoTooltipContent userId={userId} />}
    >
      {avatarVisual}
    </InfoTooltip>
  );
};

export const Avatar = memo(AvatarComponent);
